import { Login, Dashboard } from "@modules/index";
import { Redirect } from "@modules/Auth/Redirect";
import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useHasPanelAccess } from "../Hooks/useHasAccess";
import { UnAuthorized } from "@components/ErrorHandlers/UnAuthorized";
import { ModuleSelection } from "../../Components/ModuleSelection/ModuleSelection";
import { PanelDashboard } from "../Modules/ContractManagement/SubModules/Dashboard/Dashboard";
import { PanelUserManagement } from "../Modules/UserManagement/PanelUserManagement";
import { AddPanelUser } from "../Modules/UserManagement/AddPanelUser";
import { ManageCatalog } from "../Modules/ContractManagement/SubModules/ManageCatalog/ManageCatalog";
import { AddNewSORId } from "../Modules/ContractManagement/SubModules/ManageCatalog/SORIDCategory/AddSORId";
import { TemplateSelection } from "../Modules/ContractManagement/SubModules/ManageCatalog/Template/Template";
import { TemplateDrilldown } from "../Modules/ContractManagement/SubModules/ManageCatalog/WorkOrderItem/Template/Drilldown/TemplateDrilldown";
import { AddVendor } from "../Modules/ContractManagement/SubModules/ManageCatalog/WorkOrderItem/Vendor/AddVendor";
import { WorkOrder } from "../Modules/WorkOrder/WorkOrder";
import { AddWorkOrder } from "../Modules/WorkOrder/Onboarding/AddWorkOrder";
import { WorkOrderDrilldown } from "../Modules/WorkOrder/Drilldown/WorkOrderDrilldown";
import { WorkOrderApproval } from "../Modules/WorkOrderApproval/WorkOrderApproval";
import { ApprovalManagement } from "../Modules/ApprovalManagement/ApprovalManagement";
import { CreateSubsidiary } from "../Modules/ApprovalManagement/Onboarding/CreateSubsidiary";
import { CreateCostCentre } from "../Modules/ApprovalManagement/Onboarding/CreateCostCentre";
import { CreateApprover } from "../Modules/ApprovalManagement/Onboarding/CreateApprover";

interface RouteBase {
  path: string;
  element: ReactNode;
}

interface ProtectedRoutes extends RouteBase {
  roles: string[];
}

/**
 * Panel Contract roles, as `GET /user/user-access` reports them in
 * `currentRole`. These are NOT the ePOD `SPA_*` roles in Utils/Roles.
 */
export const PANEL_ROLES = {
  ADMIN: "PANEL_ADMIN",
  CREATOR: "PANEL_CREATOR",
  REVIEWER: "PANEL_REVIEWER",
};

/**
 * Master variants are grouped with the admin: they are not a lesser role, and
 * leaving them out of every list would lock those users out of the module
 * entirely.
 */
const ADMIN_ONLY = [PANEL_ROLES.ADMIN, "PANEL_MASTER_ADMIN"];
/** Raising a work order and approving one are different jobs. */
const APPROVES = [...ADMIN_ONLY, PANEL_ROLES.REVIEWER, "PANEL_MASTER_REVIEWER"];
const EVERY_PANEL_ROLE = [...APPROVES, PANEL_ROLES.CREATOR];

export const protectRoutes: ProtectedRoutes[] = [
  {
    path: `/workorder-dashboard`,
    element: <PanelDashboard />,
    roles: EVERY_PANEL_ROLE,
  },
  {
    path: `/workorder`,
    element: <WorkOrder />,
    roles: EVERY_PANEL_ROLE,
  },
  {
    path: `/workorder/add`,
    element: <AddWorkOrder />,
    roles: EVERY_PANEL_ROLE,
  },
  {
    path: `/workorder/view/:id`,
    element: <WorkOrderDrilldown />,
    roles: EVERY_PANEL_ROLE,
  },
  {
    path: `/workorder-approval`,
    element: <WorkOrderApproval />,
    roles: APPROVES,
  },
  {
    // Same drilldown as /workorder/view/:id — it switches to the approver
    // action bar based on the path (see hasKeyString("approval")).
    path: `/workorder-approval/view/:id`,
    element: <WorkOrderDrilldown />,
    roles: APPROVES,
  },
  {
    path: `/approval-management`,
    element: <ApprovalManagement />,
    roles: ADMIN_ONLY,
  },
  {
    path: `/approval-management/subsidiary/create`,
    element: <CreateSubsidiary />,
    roles: ADMIN_ONLY,
  },
  {
    // Same form as create; `?id=` switches it to edit.
    path: `/approval-management/subsidiary/edit`,
    element: <CreateSubsidiary />,
    roles: ADMIN_ONLY,
  },
  {
    path: `/approval-management/cost-centre/create`,
    element: <CreateCostCentre />,
    roles: ADMIN_ONLY,
  },
  {
    path: `/approval-management/cost-centre/edit`,
    element: <CreateCostCentre />,
    roles: ADMIN_ONLY,
  },
  {
    path: `/approval-management/approver/create`,
    element: <CreateApprover />,
    roles: ADMIN_ONLY,
  },
  {
    path: `/approval-management/approver/edit`,
    element: <CreateApprover />,
    roles: ADMIN_ONLY,
  },
  {
    path: `/workorder-user-management`,
    element: <PanelUserManagement />,
    roles: ADMIN_ONLY,
  },
  {
    path: `/workorder-user-management/add`,
    element: <AddPanelUser />,
    roles: ADMIN_ONLY,
  },
  {
    path: `/workorder-manage-catalog`,
    element: <ManageCatalog />,
    roles: ADMIN_ONLY,
  },
  {
    path: `/workorder-manage-catalog/sorid-add`,
    element: <AddNewSORId />,
    roles: ADMIN_ONLY,
  },
  {
    path: `/workorder-manage-catalog/sorid-edit`,
    element: <AddNewSORId />,
    roles: ADMIN_ONLY,
  },
  {
    path: `/workorder-manage-catalog/template-edit`,
    element: <TemplateSelection />,
    roles: ADMIN_ONLY,
  },
  {
    path: `/workorder-manage-catalog/template-view/:id`,
    element: <TemplateDrilldown />,
    // Same as the rest of Manage Catalog; it predates the role gating.
    roles: ADMIN_ONLY,
  },
  {
    path: `/workorder-manage-catalog/vendor-add`,
    element: <AddVendor />,
    roles: ADMIN_ONLY,
  },
  {
    path: `/workorder-manage-catalog/vendor-edit`,
    element: <AddVendor />,
    roles: ADMIN_ONLY,
  },
];

export const globalRoutes = [
  {
    path: "/module-selection",
    element: <ModuleSelection />,
  },
  {
    path: "/auth/login",
    element: <Login />,
  },
  {
    path: "/authorize/redirect",
    element: <Redirect />,
  },
  {
    path: "/unauthorized-entry",
    element: <UnAuthorized />,
  },
];
